import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_URL, NAV_ITEMS } from "@/constants/site";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCompanyOpen(false);
    setMobileCompanyOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !mobileOpen;

  const linkColor = transparent
    ? "text-background-100 hover:text-accent-300"
    : "text-foreground-800 hover:text-accent-700";
  const activeColor = transparent ? "text-accent-300" : "text-accent-700";

  return (
    <header
      className={`fixed top-0 left-0 z-50 w-full transition-all duration-500 ${
        transparent
          ? "bg-transparent"
          : "border-b border-background-200 bg-background-50/95 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-4 md:px-12 lg:px-20">
        <Link to="/" className="flex shrink-0 items-center" aria-label="REVRA Group トップページ">
          <img
            src={LOGO_URL}
            alt="REVRA Group ロゴ"
            className={`h-9 w-auto transition-all duration-500 md:h-11 ${
              transparent ? "" : "brightness-0"
            }`}
          />
        </Link>

        <nav className="hidden items-center gap-9 lg:flex" aria-label="メインナビゲーション">
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);

            if (item.children) {
              return (
                <div
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => setCompanyOpen(true)}
                  onMouseLeave={() => setCompanyOpen(false)}
                >
                  <Link
                    to={item.path}
                    className={`flex items-center gap-1.5 font-body text-[13px] tracking-[0.14em] transition-colors ${
                      isActive ? activeColor : linkColor
                    }`}
                  >
                    {item.label}
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${
                        companyOpen ? "rotate-180" : ""
                      }`}
                    />
                  </Link>
                  <div
                    className={`absolute left-1/2 top-full w-56 -translate-x-1/2 pt-4 transition-all duration-300 ${
                      companyOpen
                        ? "visible translate-y-0 opacity-100"
                        : "invisible -translate-y-2 opacity-0"
                    }`}
                  >
                    <div className="border border-background-200 bg-background-50 py-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-background-100"
                        >
                          <span className="font-body text-[13px] text-foreground-800">
                            {child.label}
                          </span>
                          <span className="font-label text-xs tracking-[0.18em] text-secondary-500">
                            {child.en}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`font-body text-[13px] tracking-[0.14em] transition-colors ${
                  isActive ? activeColor : linkColor
                }`}
              >
                {item.label}
              </Link>
            );
          })}
          <Link
            to="/contact"
            className={`whitespace-nowrap border px-6 py-2.5 font-body text-[12px] tracking-[0.18em] transition-colors ${
              transparent
                ? "border-background-100/60 text-background-50 hover:bg-background-50 hover:text-foreground-950"
                : "border-foreground-950 bg-foreground-950 text-background-50 hover:bg-transparent hover:text-foreground-950"
            }`}
          >
            CONTACT
          </Link>
        </nav>

        <button
          type="button"
          aria-label="メニューを開く"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((prev) => !prev)}
          className="flex h-10 w-10 items-center justify-center lg:hidden"
        >
          <i
            className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl ${
              transparent ? "text-background-50" : "text-foreground-950"
            }`}
          />
        </button>
      </div>

      <div
        className={`overflow-hidden border-t border-background-200 bg-background-50 transition-[max-height] duration-500 lg:hidden ${
          mobileOpen ? "max-h-[80vh]" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-6 py-4">
          {NAV_ITEMS.map((item) => {
            if (item.children) {
              return (
                <div key={item.path} className="border-b border-background-200">
                  <button
                    type="button"
                    onClick={() => setMobileCompanyOpen((prev) => !prev)}
                    className="flex w-full items-center justify-between py-4 font-body text-sm tracking-[0.1em] text-foreground-900"
                  >
                    {item.label}
                    <i
                      className={`ri-add-line text-lg transition-transform duration-300 ${
                        mobileCompanyOpen ? "rotate-45" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-[max-height] duration-500 ${
                      mobileCompanyOpen ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <div className="flex flex-col pb-2 pl-4">
                      <Link
                        to={item.path}
                        className="py-2.5 font-body text-[13px] text-foreground-600"
                      >
                        企業情報トップ
                      </Link>
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="py-2.5 font-body text-[13px] text-foreground-600"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className="border-b border-background-200 py-4 font-body text-sm tracking-[0.1em] text-foreground-900"
              >
                {item.label}
              </Link>
            );
          })}
          <Link
            to="/contact"
            className="mt-5 whitespace-nowrap bg-foreground-950 px-6 py-3 text-center font-body text-xs tracking-[0.2em] text-background-50"
          >
            CONTACT
          </Link>
        </nav>
      </div>
    </header>
  );
}